import { cn } from '@/lib/cn';
import {
  activeSlideContextAtom,
  selectedSlideAtom,
  selectedSlideIndexAtom,
} from '@heidi/agents/data';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { typographyVariant } from '@heidi/design-system/typography';
import { useAtom, useAtomValue } from 'jotai';
import { Plus, X } from 'lucide-react';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { presentationPreviewAtom } from './presentationPreviewAtom';

import { slideBuilderMessages } from './slide-builder.messages';

/**
 * SlideContextChip - Compact badge shown above the chat input when a slide is
 * selected in the presentation editor.
 *
 * - Inactive: "Slide N  +" — clicking "+" attaches slide as chat context.
 * - Active: filled badge "Slide N  x" — slide is targeted for the next message.
 */
export const SlideContextChip = () => {
  const intl = useIntl();
  const preview = useAtomValue(presentationPreviewAtom);
  const selectedSlide = useAtomValue(selectedSlideAtom);
  const selectedIndex = useAtomValue(selectedSlideIndexAtom);
  const [activeContext, setActiveContext] = useAtom(activeSlideContextAtom);

  const isActive = activeContext !== null;
  const displayIndex = isActive ? activeContext.slideIndex : selectedIndex;

  const ariaLabel = intl.formatMessage(
    isActive
      ? slideBuilderMessages.removeSlideContextAria
      : slideBuilderMessages.targetThisSlideAria
  );

  const tooltipText = intl.formatMessage(
    isActive
      ? slideBuilderMessages.changesApplyTooltip
      : slideBuilderMessages.applyNextMessageTooltip
  );

  const handleClick = useCallback(() => {
    if (isActive) {
      setActiveContext(null);
      return;
    }
    if (!selectedSlide) return;
    setActiveContext({
      slideIndex: selectedIndex,
      slideId: selectedSlide.id,
      title: selectedSlide.title,
    });
  }, [isActive, selectedSlide, selectedIndex, setActiveContext]);

  if (!preview.isOpen || (!selectedSlide && !isActive)) return null;

  return (
    <div className="px-3 pb-1 pt-2">
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleClick}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border transition-colors duration-150 ease-out',
                'h-7 pl-2.5 pr-1.5 outline-none',
                isActive
                  ? 'border-border-selected bg-surface-accent-hover text-text-primary hover:bg-background-secondary'
                  : 'border-border bg-surface text-text-secondary hover:border-border-hover hover:bg-surface-accent'
              )}
              aria-label={ariaLabel}
            >
              <span
                className={cn(
                  typographyVariant({ variant: 'caption' }),
                  'whitespace-nowrap'
                )}
              >
                <FormattedMessage
                  {...slideBuilderMessages.contextChipSlideLabel}
                  values={{ slideNumber: displayIndex + 1 }}
                />
              </span>

              <span
                className={cn(
                  'flex size-4 items-center justify-center rounded-full',
                  isActive ? 'text-text-secondary' : 'text-text-tertiary'
                )}
              >
                {isActive ? <X size={11} /> : <Plus size={11} />}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={6}>
            <TooltipArrow />
            {tooltipText}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
